/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, EventEmitter, Output} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {commitValueToRow} from './ag-grid-cell-editor.util';

@Component({
    selector: 'app-multi-select-dropdown-cell-editor',
    template: `
        @if (maxSelectionCount === 1) {
          <!-- Single-select dropdown -->
          <p-select [options]="availableOptions"
            [ngModel]="selectedOption"
            (ngModelChange)="onOptionChange($event)"
            (onHide)="onDropdownHide()"
            [appendTo]="overlayAppendTarget"
            [filter]="enableFilter"
            [showClear]="allowClear"
            [style]="{'max-width': '100%'}" placeholder="Select option">
          </p-select>
        } @else {
          <!-- Multi-select dropdown -->
          <p-multiSelect [options]="availableOptions"
            [ngModel]="selectedOptions"
            (ngModelChange)="onOptionsChange($event)"
            (onHide)="onDropdownHide()"
            [appendTo]="overlayAppendTarget"
            defaultLabel="Select options" [style]="{'max-width': '100%'}"
            display="chip">
          </p-multiSelect>
        }
        `,
    standalone: false
})
export class MultiSelectDropdownCellEditorComponent implements ICellEditorAngularComp {
    public overlayAppendTarget: 'body' = 'body';
    public selectedOption: string | {label: string, value: string} | null = null;
    public selectedOptions: (string | {label: string, value: string})[] = [];
    public availableOptions: {label: string, value: string}[] = [];
    public maxSelectionCount = 1;
    public enableFilter = false;
    public allowClear = false;
    private params: ICellEditorParams | null = null;
    @Output() valueChange = new EventEmitter<any>();

    agInit(params: ICellEditorParams): void {
        this.params = params;
        // availableOptions contains the string values for the dropdown
        // For ListManager structure, allowedValues contains { label, description } objects
        const availableOptions = params.colDef.cellEditorParams.availableOptions || [];
        const allowedValues = params.colDef.cellEditorParams.allowedValues || [];
        const isListManagerStructure = params.colDef.cellEditorParams.isListManagerStructure || false;

        this.availableOptions = availableOptions.map(opt => {
            if (isListManagerStructure) {
                // Find matching LabelDescription object
                const matchingOption = allowedValues.find((item: any) => item && item.label === opt);
                return {
                    label: opt,
                    value: opt,
                    description: matchingOption ? (matchingOption.description || '') : ''
                };
            } else {
                return {
                    label: opt,
                    value: opt,
                    description: ''
                };
            }
        });

        this.maxSelectionCount = params.colDef.cellEditorParams.maxSelectionCount ?? 1;
        this.enableFilter = params.colDef.cellEditorParams.filter ?? false;
        this.allowClear = params.colDef.cellEditorParams.allowClear ?? false;

        if (this.maxSelectionCount === 1) {
            // For single select, set selectedOption
            // First, normalize the value to a string (in case it's an object from old data)
            let value: string | null = null;
            if (params.value != null) {
                if (typeof params.value === 'string') {
                    value = params.value;
                } else {
                    value = String(params.value);
                }
            }
            // Find the matching option object from availableOptions
            if (value) {
                const matchingOption = this.availableOptions.find(opt => opt.value === value);
                this.selectedOption = matchingOption || value;
            } else {
                this.selectedOption = null;
            }
        } else {
            // For multi-select, normalize the value to a string first
            let valueStr: string | null = null;
            if (params.value != null) {
                if (typeof params.value === 'string') {
                    valueStr = params.value;
                } else if (Array.isArray(params.value)) {
                    // If it's an array, join it
                    valueStr = params.value.map(v => String(v)).join('; ');
                } else {
                    valueStr = String(params.value);
                }
            }
            if (valueStr) {
                const selectedValues = valueStr.split(/; ?/).filter(v => v.trim());
                this.selectedOptions = selectedValues.map(val => {
                    const matchingOption = this.availableOptions.find(opt => opt.value === val.trim());
                    return matchingOption || val.trim();
                });
            } else {
                this.selectedOptions = [];
            }
        }
    }

    getValue() {
        let rawValue: any;

        if (this.maxSelectionCount === 1) {
            // Return the selected single option value
            // PrimeNG dropdown returns the option object, so extract the value
            if (this.selectedOption != null) {
                if (typeof this.selectedOption === 'object' && 'value' in this.selectedOption) {
                    const optionObj = this.selectedOption as {value: string};
                    rawValue = optionObj.value;
                } else {
                    rawValue = this.selectedOption;
                }
            } else {
                rawValue = null;
            }
        } else {
            // Return the selected options as a joined string
            // PrimeNG multiSelect returns array of option objects, so extract values
            const values = this.selectedOptions.map(opt => {
                if (opt != null) {
                    if (typeof opt === 'object' && 'value' in opt) {
                        const optionObj = opt as {value: string};
                        return optionObj.value;
                    }
                    // If it's already a string, return it as-is
                    if (typeof opt === 'string') {
                        return opt;
                    }
                }
                return String(opt);
            }).filter(v => v); // Filter out empty values
            rawValue = values.join('; ');
        }

        // IMPORTANT: Use params.parseValue() if available to ensure valueParser is called
        // This is required for ag-grid to properly apply valueParser when using custom editors
        if (this.params?.parseValue) {
            return this.params.parseValue(rawValue);
        }

        return rawValue;
    }

    onOptionChange(value: any): void {
        // PrimeNG returns the option object, so update our selectedOption
        this.selectedOption = value;
        const newValue = this.getValue();
        commitValueToRow(this.params, newValue);
        this.valueChange.emit(newValue);
    }

    onOptionsChange(values: any[]): void {
        // PrimeNG returns array of option objects, so update our selectedOptions
        this.selectedOptions = values || [];
        const newValue = this.getValue();
        commitValueToRow(this.params, newValue);
        this.valueChange.emit(newValue);
    }

    onDropdownHide(): void {
        // Queue stopEditing so ngModel updates settle first.
        queueMicrotask(() => {
            if (this.params) {
                this.params.stopEditing();
            }
        });
    }

    refresh(params: any): boolean {
        return false;
    }

    // Required for ag-grid to recognize this as an inline editor
    isPopup(): boolean {
        return false;
    }

}

